import { Injectable } from '@angular/core';
import { Observable, Subject, of, throwError } from 'rxjs'; // For rxjs 6
import { HttpClient } from '@angular/common/http';
import { IonicComponentService } from './ionic-component.services';
import { catchError, retryWhen, delay, scan } from 'rxjs/operators';
import {
  CartArticle,
  CartArticleOption,
  DraftReceiptResponse,
  LocationResponse,
  NewPaymentResponse,
  OpeningTime,
  OrderResponse,
  OrderStatusResponse,
  PaymentMethod,
  PaymentResponse,
  ProductOptionProduct,
  Shop,
  Table,
  TimeSlot,
} from 'src/models/order.models';
import { ReceiptPayloadBuilder } from './receipt-payload.builder';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ShopService {
  shopID = 0;
  articleID = 0;
  article: CartArticle[] = [];
  articleList: CartArticle[] = [];
  currentPrice = 0;
  shopName = '';
  order = 0;
  orderId = '';
  tableId = '';
  tableNumber = 0;
  tableFound = false;
  payment = '';
  receiptPrefix = '';
  language = 'nl';
  paymentMethod = '';
  paymentUrl = '';
  enableTableService = false;
  enableDiscount = false;
  enableTimeSlots = false;
  askEmail = false;
  askEmployeeNumber = false;
  selectedTimeSlot: TimeSlot | null = null;
  timeSlots: TimeSlot[] = [];
  voucherCode = '';
  email = '';
  employeeNumber = '';
  public isPriceLoading = true;


  modalOpen = false;

  // Location info
  slug = '';
  locationId = 0;

  currentVersion = '1.1.9';
  currentVersionNumber = 119;

  place = 0;

  public responsedata: DraftReceiptResponse | PaymentResponse | NewPaymentResponse | null = null;
  public tables: Table[] = [];
  public paymentMethods: PaymentMethod[] = [];

  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly mobileOrderBaseUrl = environment.mobileOrderBaseUrl;

  constructor(
    private http: HttpClient,
    private ionicComponentService: IonicComponentService
  ) { }

  private buildUrl(path: string, queryParams?: Record<string, string | number | boolean>) {
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    let url = this.apiBaseUrl + normalizedPath;

    if (queryParams) {
      const query = Object.keys(queryParams)
        .filter((key) => queryParams[key] !== undefined && queryParams[key] !== null)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(queryParams[key]))}`)
        .join('&');

      if (query) {
        url += (url.includes('?') ? '&' : '?') + query;
      }
    }

    return url;
  }

  getApi<T>(path: string, queryParams?: Record<string, string | number | boolean>) {
    return this.http.get<T>(this.buildUrl(path, queryParams));
  }

  getApiResponse<T>(path: string, queryParams?: Record<string, string | number | boolean>) {
    return this.http.get<T>(this.buildUrl(path, queryParams), { observe: 'response' });
  }

  postApi<T>(path: string, body: unknown = {}, queryParams?: Record<string, string | number | boolean>) {
    return this.http.post<T>(this.buildUrl(path, queryParams), body);
  }

  getOrder(orderId: string) {
    return this.getApi<OrderResponse>(`/orders/${orderId}/`, { format: 'json' });
  }

  getOrderStatus(orderId: string) {
    return this.getApi<OrderStatusResponse>(`/orders/${orderId}/status/`, { format: 'json' });
  }

  getLocation(locationSlug: string) {
    return this.getApiResponse<LocationResponse>(`/locations/${locationSlug}/`, { format: 'json' });
  }

  getShop(shopId: number | string) {
    return this.getApi<Shop>(`/shops/${shopId}/`, { format: 'json' });
  }

  getShopCategories(shopId: number | string) {
    return this.getApi<Shop['products'][number]['category'][]>(`/shops/${shopId}/categories/`, { format: 'json' });
  }

  getShopOpeningTimes(shopId: number | string) {
    return this.getApi<OpeningTime[]>(`/shops/${shopId}/opening_times/`, { format: 'json' });
  }

  getShopTimeSlots(shopId: number | string) {
    return this.http.get<TimeSlot[]>(`${this.mobileOrderBaseUrl}/shops/${shopId}/time_slots/`);
  }

  getTable(shopId: number | string, tableId: number | string) {
    return this.getApi<Table>(`/shops/${shopId}/tables/${tableId}/`, { format: 'json' });
  }

  cleanCart() {
    this.articleList = [];
    this.currentPrice = 0;
    this.selectedTimeSlot = null;
  }

  getArticleID() {
    return this.articleID;
  }

  saveArticleID(id: number) {
    this.articleID = id;
  }

  getShopID() {
    return this.shopID;
  }

  setShopID(id: string) {
    this.shopID = parseInt(id, 10);
    this.ionicComponentService.setShopId(this.shopID);
  }

  saveShopID(id: number) {
    this.shopID = id;
    this.ionicComponentService.setShopId(id);
  }

  saveShopName(name: string) {
    this.shopName = name;
  }

  getShopName() {
    return this.shopName;
  }

  saveTableId(id: string) {
    this.tableId = id;
  }

  getLocationSlug() {
    return this.slug;
  }

  setLocationSlug(slug: string) {
    this.slug = slug;
  }

  savePaymentMethod(payment: string) {
    this.paymentMethod = payment;
  }

  getPaymentMethod() {
    return this.paymentMethod;
  }

  getEnableTableService() {
    return this.enableTableService;
  }

  getEnableDiscount() {
    return this.enableDiscount;
  }

  getAskEmail() {
    return this.askEmail;
  }

  getAskEmployeeNumber() {
    return this.askEmployeeNumber;
  }

  saveEnableTableService(enableTableService: boolean) {
    this.enableTableService = enableTableService;
  }

  saveEnableDiscount(enableDiscount: boolean) {
    this.enableDiscount = enableDiscount;
  }

  saveAskEmail(askEmail: boolean) {
    this.askEmail = askEmail;
  }

  saveAskEmployeeNumber(askEmployeeNumber: boolean) {
    this.askEmployeeNumber = askEmployeeNumber;
  }

  saveEmail(email: string) {
    this.email = email;
  }

  getEmail() {
    return this.email;
  }

  saveEmployeeNumber(employeeNumber: string) {
    this.employeeNumber = employeeNumber;
  }

  getEmployeeNumber() {
    return this.employeeNumber;
  }

  getEnableTimeSlots() {
    return this.enableTimeSlots;
  }

  saveEnableTimeSlots(enableTimeSlots: boolean) {
    this.enableTimeSlots = enableTimeSlots;
  }

  saveTimeSlots(timeSlots: TimeSlot[] = []) {
    this.timeSlots = timeSlots;
    if (this.selectedTimeSlot && !this.getAvailableTimeSlots().some((slot) => slot.id === this.selectedTimeSlot?.id)) {
      this.selectedTimeSlot = null;
    }
  }

  getTimeSlots() {
    return this.timeSlots;
  }

  getAvailableTimeSlots() {
    return this.timeSlots.filter((timeSlot) => timeSlot.has_capacity);
  }

  isRegularOrder() {
    return this.tableId === '' || this.enableTableService === false;
  }

  saveSelectedTimeSlot(timeSlot: TimeSlot | null) {
    this.selectedTimeSlot = timeSlot;
  }

  getSelectedTimeSlot() {
    return this.selectedTimeSlot;
  }

  getSelectedTimeSlotId() {
    return this.selectedTimeSlot?.id || '';
  }

  saveVoucherCode(voucherCode: string) {
    this.voucherCode = voucherCode;
  }

  getVoucherCode() {
    return this.voucherCode;
  }

  getTableId() {
    return this.tableId;
  }

  saveTables(tables: Table[]) {
    this.tables = tables;
  }

  getTables() {
    return this.tables;
  }

  savePaymentMethods(paymentMethods: PaymentMethod[]) {
    this.paymentMethods = paymentMethods;
  }

  getPaymentMethods() {
    return this.paymentMethods;
  }

  saveTableFound(found: boolean) {
    this.tableFound = found;
  }

  getTableFound() {
    return this.tableFound;
  }

  saveOrderId(id: string) {
    this.orderId = id;
  }

  getOrderId() {
    return this.orderId;
  }

  getCurrentVersion() {
    return this.currentVersion;
  }

  saveCurrentVersion(version: string) {
    this.currentVersion = version;
  }

  getCurrentVersionNumber() {
    return this.currentVersionNumber;
  }

  saveCurrentVersionNumber(version: number) {
    this.currentVersionNumber = version;
  }

  async clearAppUpdateCaches() {
    if ('caches' in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
    }

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  }

  async forceFreshNavigation(path: string, requiredVersionNumber?: number) {
    try {
      await this.clearAppUpdateCaches();
    } catch (e) {
      // Continue with the cache-busted navigation even when cache cleanup is blocked or unsupported.
    }

    const targetUrl = new URL(path, window.location.origin);
    targetUrl.searchParams.set('update', String(requiredVersionNumber || this.currentVersionNumber));
    targetUrl.searchParams.set('t', Date.now().toString());
    window.location.replace(targetUrl.toString());
  }

  saveReceiptPrefix(prefix: string) {
    this.receiptPrefix = prefix;
  }

  getReceiptPrefix() {
    return this.receiptPrefix;
  }

  saveTableNumber(tableNumber: number) {
    this.tableNumber = tableNumber;
  }

  getTableNumber() {
    return this.tableNumber;
  }

  saveLanguage(language: string) {
    this.language = language;
  }

  getLanguage() {
    return this.language;
  }

  getArticleList() {
    return this.articleList;
  }

  getOrderNumber() {
    return this.order;
  }

  getCurrentPrice() {
    return (this.currentPrice / 100).toFixed(2);
  }

  getPaymentUrl() {
    return this.paymentUrl;
  }

  addArticleToBasket(
    id: number,
    image: string | null,
    name: string,
    price: string | number,
    containsAlcohol: boolean,
    maxQuantity: number | string | null,
    ArticleOptions: CartArticleOption[],
    SelectedOptions: ProductOptionProduct[]
  ) {
    this.place += 1;
    this.articleList.push({
      place: this.place,
      ArticleId: id,
      Quantity: 1,
      ArticleOptions,
      image,
      name,
      price,
      containsAlcohol,
      max_quantity: maxQuantity,
      SelectedOptions, // De extra opties zoals saus, slagroom etc
    });
    this.postDraftReceipt();
  }

  increaseQuantity(article: CartArticle) {
    const articleElement = this.articleList.findIndex(
      (i) => i.place === article.place
    );
    this.articleList[articleElement].Quantity = article.Quantity + 1;
    this.postDraftReceipt();
  }

  decreaseQuantity(article: CartArticle) {
    const articleElement = this.articleList.findIndex(
      (i) => i.place === article.place
    );
    if (article.Quantity === 1) {
      const removed = this.articleList.splice(articleElement, 1);
    } else {
      this.articleList[articleElement].Quantity = article.Quantity - 1;
    }
    this.postDraftReceipt();
  }

  async postDraftReceipt() {
    if (this.articleList.length === 0) {
      this.currentPrice = 0;
      this.isPriceLoading = false;
      return;
    }

    this.isPriceLoading = true;
    let url = this.buildUrl(`/shops/${this.shopID}/receipt/draft/`);
    if (this.voucherCode) {
      url += '?voucher_code=' + encodeURIComponent(this.voucherCode);
    }
    url = this.addCustomerInfoQueryParams(url);
    url = this.addSelectedTimeSlotQueryParam(url);

    const maxRetries = 5;
    const retryDelay = 1000; // ms

    return new Promise((resolve) => {
      this.http.post<DraftReceiptResponse>(url, this.getReceiptPayload()).subscribe(
        (val) => {
          this.responsedata = val;
          this.currentPrice = this.responsedata.TotalAmountInclVAT;
          this.isPriceLoading = false;
          if (!this.responsedata.Success) {
            this.handleReceiptError(val, '<br>Fout bij verwerken product(en).<br>Probeer opnieuw of vraag een medewerker om hulp.');
            resolve(null);
            return;
          }
          resolve(this.responsedata);
        },
        (response) => {
          this.isPriceLoading = false;
          this.handleReceiptError(response);
          resolve(null);
        },
        () => {
          // this.ionicComponentService.dismissLoading();
        }
      );
    });
  }

  async createReceipt(method: string): Promise<PaymentResponse | null> {
    const source = window.location !== window.parent.location ? 'app' : 'qr';
    let url = this.buildUrl(`/shops/${this.shopID}/receipt/create/${method}/`, { source });

    if (this.voucherCode) {
      url += '&voucher_code=' + encodeURIComponent(this.voucherCode);
    }
    url = this.addCustomerInfoQueryParams(url);
    url = this.addSelectedTimeSlotQueryParam(url);

    return new Promise((resolve) => {
      this.http.post<PaymentResponse>(url, this.getReceiptPayload()).subscribe(
        (val) => {
          this.responsedata = val;
          this.currentPrice = val.total_price;
          this.order = val.order_id;
          if (!val.success) {
            this.handleReceiptError(val);
            resolve(null);
            return;
          }
          this.paymentUrl = val.payment_url || '';
          resolve(val);
        },
        (response) => {
          this.handleReceiptError(response);
          resolve(null);
        },
      );
    });
  }

  async createReceiptWithTable(method: string): Promise<PaymentResponse | null> {
    const source = window.location !== window.parent.location ? 'app' : 'qr';
    let url = this.buildUrl(`/shops/${this.shopID}/receipt/create/${this.tableId}/${method}/`, { source });

    if (this.voucherCode) {
      url += '&voucher_code=' + encodeURIComponent(this.voucherCode);
    }
    url = this.addCustomerInfoQueryParams(url);

    return new Promise((resolve) => {
      this.http.post<PaymentResponse>(url, this.getReceiptPayload()).subscribe(
        (val) => {
          this.responsedata = val;
          this.currentPrice = val.total_price;
          this.order = val.order_id;
          if (!val.success) {
            this.handleReceiptError(val);
            resolve(null);
            return;
          }
          this.paymentUrl = val.payment_url || '';
          resolve(val);
        },
        (response) => {
          this.handleReceiptError(response);
          resolve(null);
        },
      );
    });
  }

  async createNewPayment() {
    const url = this.buildUrl(`/orders/${this.orderId}/new_payment/`, { format: 'json' });
    this.http.post<NewPaymentResponse>(url, {}).subscribe(
      (val) => {
        this.responsedata = val;
        if (!val.success) {
          this.ionicComponentService.presentAlertButton(
            '<br>Fout bij verwerken.<br>Probeer opnieuw of vraag een medewerker om hulp.'
          );
        }
        this.paymentUrl = val.payment_url || '';
      },
      (response) => {
        this.ionicComponentService.presentAlertButton(
          '<br>Fout bij verwerken.<br>Probeer opnieuw of vraag een medewerker om hulp.'
        );
      },
    );
  }

  setModalSate(state: boolean) {
    this.modalOpen = state;
    return this.modalOpen;
  }

  private getReceiptPayload() {
    return ReceiptPayloadBuilder.build(this.articleList);
  }

  private addSelectedTimeSlotQueryParam(url: string) {
    const selectedTimeSlotId = this.getSelectedTimeSlotId();
    if (!this.enableTimeSlots || !selectedTimeSlotId || !this.isRegularOrder()) {
      return url;
    }

    const separator = url.includes('?') ? '&' : '?';
    return url + separator + 'time_slot_id=' + encodeURIComponent(String(selectedTimeSlotId));
  }

  private addCustomerInfoQueryParams(url: string) {
    let nextUrl = url;

    if (this.askEmail && this.email) {
      const separator = nextUrl.includes('?') ? '&' : '?';
      nextUrl += separator + 'email=' + encodeURIComponent(this.email);
    }

    if (this.askEmployeeNumber && this.employeeNumber) {
      const separator = nextUrl.includes('?') ? '&' : '?';
      nextUrl += separator + 'employee_number=' + encodeURIComponent(this.employeeNumber);
    }

    return nextUrl;
  }

  private handleReceiptError(response: any, fallbackMessage = '<br>Fout bij verwerken bestelling.<br>Probeer opnieuw of vraag een medewerker om hulp.') {
    const backendError = String(
      response?.error?.detail ||
      response?.error?.error ||
      response?.error?.message ||
      response?.error ||
      response?.message ||
      ''
    );

    if (backendError.includes('OrderingTimeSlot')) {
      this.selectedTimeSlot = null;
      this.ionicComponentService.presentCartRedirectAlertButton(
        '<br>Het gekozen tijdslot is niet meer beschikbaar.<br>Selecteer een ander tijdslot en probeer opnieuw.'
      );
      return;
    }

    this.ionicComponentService.presentAlertButton(fallbackMessage);
  }
}
